import { inject, Injectable } from "@angular/core";
import { FormArray, FormBuilder, FormGroup } from "@angular/forms";
import {
  HardFormService,
  IHardFormField,
  IHardFormFieldParams,
} from "@hardcode-kz/ui";
import { IBaseConfig } from "@models/form/base-config";
import {
  IGridPage,
  IGridPageParams,
  IGridScreen,
  IGridWidget,
  IGridWidgetParams,
  IGridWidgetSettings,
  IWidgetParams,
} from "@modules/grid/models/grid-manager";
import { InterfaceManagementApiService } from "@dmms/shared/data-access/interface-management/services/interface-management-api.service";
import { BaseService } from "@dmms/core/http";
import { catchError, Observable, of, take } from "rxjs";
import { switchMap } from "rxjs/operators";
import { AuthService } from "@dmms/core/auth";

@Injectable({
  providedIn: "root",
})
export class GridManagerService extends BaseService {
  widgets: { [key: string]: any } = {};
  apiService = inject(InterfaceManagementApiService);
  formConstructor = inject(HardFormService);
  fb = inject(FormBuilder);
  private authService = inject(AuthService);

  registerWidgets(components: { [key: string]: any }): void {
    for (const key in components) {
      if (!this.widgets[key]) {
        this.widgets[key] = components[key];
      }
    }
  }

  getCurrentUrl(): string {
    return this.router.url.replace("/", "").split("?")[0];
  }

  async getGridPage(): Promise<IGridPage> {
    const results = await this.apiService
      .getGrids(this.getCurrentUrl())
      .pipe(
        take(1),
        catchError(() => of(null)),
      )
      .toPromise();

    if (results && results?.value?.items) {
      return results.value.items.find(
        (grid) => grid.url === this.getCurrentUrl(),
      );
    }

    return null;
  }

  async getGridScreen(takeGridScreenFromRole: boolean = true): Promise<{
    pageId: string;
    url: string;
    screen: IGridScreen;
  }> {
    const gridPage = await this.getGridPage().then();

    if (gridPage) {
      const screen = this.getScreenSize();

      if (gridPage) {
        return {
          pageId: gridPage.id,
          url: gridPage.url,
          screen: this.filterGridsByScreenSize(
            gridPage.screens,
            screen.screenWidth,
            screen.screenHeight,
            takeGridScreenFromRole,
          ),
        };
      }
    }

    return null;
  }

  private filterGridsByScreenSize(
    gridsJson: IGridScreen[],
    screenWidth: number,
    screenHeight: number,
    takeGridScreenFromRole: boolean,
  ): IGridScreen {
    const gridJson = gridsJson?.find(
      (f) =>
        f.screenSize.minWidth <= screenWidth &&
        screenWidth <= f.screenSize.maxWidth &&
        f.screenSize.minHeight <= screenHeight &&
        screenHeight <= f.screenSize.maxHeight,
    );

    if (gridJson && typeof gridJson.grids !== "undefined") {
      if (takeGridScreenFromRole) {
        this.authService.currentUser$.subscribe((user: any) => {
          this.updateGrids(gridJson, user?.roleCodes || []);
        });
      }
      return gridJson;
    } else {
      return null;
    }
  }

  /* Если для роли есть индивидуальные настройки, то перезаписываем дефолтные настройки */
  updateGrids(
    gridJson: IGridScreen,
    roleCodes: Array<{ id: string; isMain?: boolean }> = [],
  ): void {
    if (!roleCodes?.length || !gridJson?.grids?.length) return;

    const mainRoleId =
      roleCodes.find((rc) => rc?.isMain)?.id || roleCodes[0]?.id;

    for (const grid of gridJson.grids) {
      const rolesParams = grid?.params?.rolesParams;
      if (!rolesParams?.length) continue;

      let matched = mainRoleId
        ? rolesParams.find((rp) =>
            rp.params.roles?.some((role) => role.id === mainRoleId),
          )
        : undefined;

      if (matched) {
        const { roles, rolesParams, ...restParams } = matched.params;
        Object.assign(grid.params, restParams);
      }
    }
  }

  getScreenSize(): { screenWidth: number; screenHeight: number } {
    const body = document.getElementsByTagName("body")[0];
    const screenWidth = body.clientWidth;
    const screenHeight = body.clientHeight;

    return { screenWidth, screenHeight };
  }

  getComponent(component: string) {
    return this.widgets[component];
  }

  buildFormControlsRecursively(
    items: IWidgetParams | IGridWidgetSettings,
    formGroup: FormGroup,
    config: IBaseConfig,
  ) {
    for (const item in items) {
      const field = this.getFieldConfigByName(item, config.fields);

      if (field && field?.params && field.params?.fields) {
        const formArray = formGroup.get(field.name) as FormArray;

        if (items[item].length) {
          for (let i = 0; i < items[item].length; i++) {
            const group = this.fb.group([]);

            this.formConstructor.addControlsToForm(field.params.fields, group);
            formArray.push(group);

            this.buildFormControlsRecursively(items[item][i], group, config);
          }
        }
      }
    }
  }

  getFieldConfigByName(
    fieldName: string,
    fields: IHardFormField<any>[],
  ): IHardFormField<IHardFormFieldParams> | undefined {
    for (const fieldKey in fields) {
      const field: IHardFormField<any> = fields[fieldKey];

      if (field.name == fieldName) {
        return field;
      }

      if (field?.params && field.params?.fields) {
        const nestedField = this.getFieldConfigByName(
          fieldName,
          field.params.fields,
        );
        if (nestedField) {
          return nestedField;
        }
      }
    }

    return undefined;
  }

  createWidgetFlow(data: Partial<IGridWidget>): Observable<any> {
    if (!data.pageId) {
      return this.createPage(data);
    } else if (!data.screenId && data.pageId) {
      return this.createScreen(data.pageId, data);
    } else if (data.screenId && data.pageId && !data.widgetId) {
      return this.createWidget(data.screenId, data);
    } else {
      return of(null);
    }
  }

  createPage(data: Partial<IGridWidget>): Observable<any> {
    const params: IGridPageParams = {
      title: data.title,
      url: this.getCurrentUrl(),
    };

    return this.apiService.createGridPage(params).pipe(
      take(1),
      catchError((error) => {
        console.error("Ошибка при создании страницы:", error);
        return of(null);
      }),
      switchMap((res) => {
        if (res?.isSuccess && res.value) {
          return this.createScreen(res.value, data);
        } else {
          return of(null);
        }
      }),
    );
  }

  createScreen(pageId: string, data: Partial<IGridWidget>): Observable<any> {
    const screenParams = data.screen;

    return this.apiService.createGridScreen(pageId, screenParams).pipe(
      take(1),
      catchError((error) => {
        console.error("Ошибка при создании экрана:", error);
        return of(null);
      }),
      switchMap((screenRes) => {
        if (screenRes?.isSuccess && screenRes.value) {
          return this.createWidget(screenRes.value, data);
        } else {
          return of(null);
        }
      }),
    );
  }

  createWidget(screenId: string, data: Partial<IGridWidget>): Observable<any> {
    const widgetParams: IGridWidgetParams = {
      component: data.component,
      settings: data.settings,
      params: data.params,
      configuration: data.configuration,
      title: data.title,
    };

    return this.apiService.createWidget(screenId, widgetParams).pipe(
      take(1),
      catchError((error) => {
        console.error("Ошибка при создании виджета:", error);
        return of(null);
      }),
      switchMap((widgetRes) => {
        if (widgetRes?.isSuccess && widgetRes?.value) {
          const value = { ...widgetRes.value, screenId };
          return of({ ...widgetRes, value });
        }
        return of(null);
      }),
    );
  }
}
